import {
    ChangeDetectionStrategy,
    Component,
    effect,
    inject,
    input,
    InputSignal,
    OnInit,
    output,
    OutputEmitterRef,
    signal,
    untracked,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { RessourcesModule } from '@ressources/client/ressources.module';
import { FormService } from '@shared/client/services/form.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ReactiveFormsModule, RessourcesModule],
    selector: 'app-input-number',
    styleUrl: './input-number.component.scss',
    templateUrl: './input-number.component.html',
})
export class InputNumberComponent implements OnInit {
    public readonly additionalClasses = input<string>('');
    public readonly disabled: InputSignal<boolean> = input<boolean>(false);
    public readonly errors: InputSignal<string[]> = input<string[]>([]);
    public readonly inputFormControl: InputSignal<FormControl | undefined> = input<FormControl | undefined>();
    public inputId: string = crypto.randomUUID();
    public readonly label: InputSignal<string | undefined> = input<string | undefined>();
    public readonly labelTooltip = input<string | undefined>();
    public readonly max: InputSignal<number> = input<number>(Number.MAX_SAFE_INTEGER);
    public readonly min: InputSignal<number> = input<number>(Number.MIN_SAFE_INTEGER);
    public onBlur: OutputEmitterRef<number> = output<number>();
    public onValueChange: OutputEmitterRef<number> = output<number>();
    public readonly placeholder: InputSignal<string | undefined> = input<string>();
    public readonly valueTooltip = input<string | undefined>();

    protected readonly formService = inject(FormService);

    private readonly blurOutValue = signal<string | undefined>(undefined);

    public constructor() {
        effect(() => {
            if (untracked(() => !this.inputFormControl())) {
                return;
            }
            if (this.disabled()) {
                untracked(() => this.inputFormControl()!.disable());

                return;
            }

            untracked(() => this.inputFormControl()!.enable());
        });
    }

    public handleOnBlur(): void {
        if (this.blurOutValue() === this.inputFormControl()?.value) {
            return;
        }

        this.blurOutValue.set(this.inputFormControl()?.value);
        this.onBlur.emit(this.inputFormControl()?.value);
    }

    public ngOnInit(): void {
        this.blurOutValue.set(this.inputFormControl()?.value);
    }

    public onChange(event: Event): void {
        const raw = (event.target as HTMLInputElement).value;
        const parsed = Number.parseFloat(raw);

        if (raw === '' || Number.isNaN(parsed)) {
            this.inputFormControl()?.setValue(null);
        }

        this.onValueChange.emit(parsed);
    }
}
